import React, { FC,useRef,useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Container,
  TopInfoWrapper,
  HeadWrapper,
  RightHeadContainer,
  InstanceName,
  NavWrapper,
  BodyWrapper,
} from './styles'
import {
  InfoBlock,
  BackArrow,
  Button,
  TopProgress,
  ModalVariables,
  InfoSkeleton,
  DropOptions
} from '../../../../components'
import { useNavigate, useParams } from 'react-router-dom'
import MenuButton from '../../../../components/button/menu-button'
import { icons } from '../../../../icons'
import { PropsContainer } from '../../../../components/table/styles'
import { flavorService } from '../../../../api'
import { useUi } from '../../../../context/ui.context'
import {formatFileSize} from '../../../../utils/sizes'
import {timeConverterMMDDYYHHMM} from '../../../../utils/dates'
import { handleOsIcons } from '../../../../hooks/useOsIcons'
import { useLocalStorage } from '../../../../hooks/useLocalStorage'
import Skeleton from 'react-loading-skeleton'
import config from '../../../../config'
import { InstanceDot, InstanceHeadInfo, InstanceStatus } from '../../Instances/components/instance-page/styles'
import { handleStatusName } from '../../../../utils/handle-status-name'
import { usePageTitle } from '../../../../context/navigationContext'

const FlavorPage:FC = () => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const {flavorId} = useParams()
  const {viewAlert} = useUi()
  const [options, setOptions] = useState<boolean>(false)
  const [flavor, setFlavor] = useState<any>()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const {setDataToLs} = useLocalStorage()
  usePageTitle(flavor ? `${t("PAGE_TITLE_FLAVOR_DETAIL")}: ${flavor?.display_name || flavor?.name || flavor?.id}` : t("PAGE_TITLE_FLAVOR_DETAIL"))
  const dropsRef:any = useRef()
  const optionsRef:any = useRef()

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  },[]);
  
  const handleClickOutside = (event:any) => {
    if (optionsRef && !optionsRef.current.contains(event.target) && !dropsRef.current.contains(event.target)){
      setOptions(false)
    }
  }
  
  const _updateFlavor = () => {
    if(flavorId) {
      flavorService.getFlavor(flavorId)
      .then((res) => {
        setFlavor(res.data)
        setPageLoading(false)
      })
      .catch((err) => {
        if(err?.response?.data?.detail){
          viewAlert({
            severity: 'error',
            message: err.response?.data?.detail
          })
        }
        navigate('/openstack/flavors')
        setPageLoading(false)
      })
    }
  }
  
  useEffect(() => {
    _updateFlavor()
    const interval = setInterval(() => {
      _updateFlavor()
    },5000)
    return () => {
      clearInterval(interval)
    }
  },[])
  
  const specifications = [
    {
      title:t("ID"),
      id:'1',
      value:flavor?.id,
      copy:true,
    },
    {
      title:t("FLAVOR_ID"),
      id:'2',
      value:flavor?.flavor_id,
      copy:true,
    },
    {
      title:t("DISPLAY_NAME"),
      id:'3',
      value:flavor?.display_name || flavor?.name
    },
    {
      title:t("NAME"),
      id:'4',
      value:flavor?.name
    },
    {
      title:t("DESCRIPTION"),
      id:'5',
      value:flavor?.description || t("NO_DESCRIPTION")
    },
    {
      title:t("VCPUS"),
      id:'6',
      value:flavor?.vcpus
    },
    {
      title:t("MEMORY_MB"),
      id:'7',
      value:`${(flavor?.memory_mb / 1024).toFixed(1)} GB (${flavor?.memory_mb} MB)`
    },
    {
      title:t("ROOT_DISK_GB"),
      id:'8',
      value:flavor?.root_gb
    },
    {
      title:t("EPHEMERAL_DISK_GB"),
      id:'9',
      value:flavor?.ephemeral_gb
    },
    {
      title:t("SWAP_MB"),
      id:'10',
      value:flavor?.swap
    },
    {
      title:t("IS_PUBLIC"),
      id:'11',
      value:flavor?.is_public ? t("YES") : t("NO")
    },
    {
      title:t("SHOW_FOR_END_USER"),
      id:'12',
      value:flavor?.show_for_end_user ? t("YES") : t("NO")
    },
    {
      title:t("USE_FOR_DBAAS"),
      id:'13',
      value:flavor?.use_for_dbaas ? t("YES") : t("NO")
    },
    {
      title:t("OUT_OF_STOCK"),
      id:'14',
      value:flavor?.out_of_stock ? t("YES") : t("NO")
    },
    {
      title:t("DISABLED"),
      id:'15',
      value:flavor?.disabled ? t("YES") : t("NO")
    },
    {
      title:t("REGION"),
      id:'16',
      value:flavor?.region
    },
  ]

  const items = [
    {
      title:t('EDIT'),
      id:'3',
      icon:icons.Edit,
      onClick: () => {
        navigate(`edit`)
      },
      divider:false
    },
    {
      title:t("DELETE"),
      id:'4',
      onClick:() => {
        openModal('delete')
        setAlertTitle(t("FLAVOR"))
        setAlertSubTitle([flavor])
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]

  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }
  
  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }
  
  const _deleteFlavor = async () => {
    if(flavor){
      try {
        setLoading(true);
        await flavorService.deleteFlavor(flavor.id);
        setViewModal(false)
        setActiveAnimation(false)
        navigate('/openstack/flavors')
      } catch (e: any) {
        console.log('e',e);
        if (e?.response?.data.detail) {
          viewAlert({
            severity: 'error',
            message: e.response.data.detail,
          });
        }
      } finally {
        setLoading(false);
      }
    }
  }

  const closeModal = (props:any) => {
    if(modalType === 'delete'){
      _deleteFlavor()
    }
  }
  
  if(pageLoading || !flavor) return (
    <Container>
      <TopProgress loading={pageLoading}/>
      <BodyWrapper>
        <InfoSkeleton size={9}/>
        <InfoSkeleton size={4}/>
      </BodyWrapper>
    </Container>
  )
  
  return(
    <Container>
      <BackArrow fallbackPath="/openstack/flavors"/>
      <HeadWrapper>
        <div>
          <InstanceName>
            {flavor?.display_name || flavor?.name || flavor?.id}
          </InstanceName>
          <InstanceHeadInfo style={{margin:"0px"}}>
            <InstanceDot active={!flavor.out_of_stock ? "available" : "out_of_stock"}/>
            <InstanceStatus active={!flavor.out_of_stock ? "available" : "out_of_stock"}>
              {handleStatusName(flavor.out_of_stock ? "out_of_stock" : "available", t)}
            </InstanceStatus>
          </InstanceHeadInfo>
        </div>
        <RightHeadContainer>
          <div ref={dropsRef}>
            <Button
              variant="stroke"
              size='small'
              icon={icons.More}
              isFill
              title={t("MENU")}
              onClick={() => setOptions(!options)}
            />
          </div>
          <PropsContainer style={{top:'35px', right:'0px'}} ref={optionsRef} active={options}>
            <DropOptions
              options={items}
            />
          </PropsContainer>
        </RightHeadContainer>
      </HeadWrapper>
      <TopInfoWrapper>
        <InfoBlock
          data={specifications}
          title={t("FLAVOR_SPECIFICATIONS")}
          customStyles={{ width:"545px", flex:'auto 1', marginRight:'10px'}}
        />
        <InfoBlock
          data={[
            {
              title: t("TOTAL_MEMORY"),
              value: `${(flavor?.memory_mb / 1024).toFixed(1)} GB (${flavor?.memory_mb} MB)`
            },
            {
              title: t("TOTAL_DISK"),
              value: `${flavor?.root_gb + flavor?.ephemeral_gb} GB`
            },
            {
              title: t("TOTAL_SWAP"),
              value: `${flavor?.swap} MB`
            }
          ]}
          title={t("RESOURCE_SUMMARY")}
          customStyles={{ width:"545px", flex:'auto 1', marginRight:'10px'}}
        />
      </TopInfoWrapper>
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
        instance={flavor}
      />
    </Container>
  )
}

export default FlavorPage
